import React from 'react';
import { Switch } from 'shared/ui/Switch';
import { Donut, type DonutProps } from './Donut';
import { Tally, type TallyProps } from './Tally';

export type StatBlockProps = {
  sets: (DonutProps & { id: string })[];
  tally: TallyProps;
  isPBUnit: boolean;
  onUnitToggle: (next: boolean) => void;
  /** System-wide controls the shell places beside the unit switch (the sizing mode). */
  controls?: React.ReactNode;
};

const LEGEND = [
  { label: 'flash useable', swatch: 'bg-flash' },
  { label: 'disk useable', swatch: 'bg-brand' },
  { label: 'protection overhead', swatch: 'bg-hairline/20' },
];

/** The head of the rail: one capacity ring per storage set, the legend that explains
 *  their colours, and the node tally. Presentational — useLiveRail derives every figure. */
export function StatBlock({ sets, tally, isPBUnit, onUnitToggle, controls }: StatBlockProps) {
  return (
    <section className="shrink-0 overflow-hidden rounded-[var(--radius-2)] border border-hairline/15 bg-surface">
      <div className="flex items-center gap-2 border-b border-hairline/15 px-3 py-2">
        <span className="h-2 w-2 shrink-0 rounded-full bg-flash shadow-[0_0_8px_rgb(var(--c-flash))] motion-safe:animate-pulse" aria-hidden="true" />
        <h3 className="font-display text-sm font-semibold text-ink">System</h3>
        <span className="font-body text-tiny text-ink-muted">Live summary</span>
      </div>

      <div data-testid="statblock-controls" className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline/15 px-3 py-2">
        {controls}
        <Switch
          className="ml-auto"
          checked={!isPBUnit}
          onChange={onUnitToggle}
          ariaLabel="Capacity units: decimal or binary"
          offLabel="PB"
          onLabel="PiB"
        />
      </div>

      <div className="grid grid-cols-2 gap-3 px-3 pt-4">
        {sets.map(({ id, ...set }) => <Donut key={id} {...set} />)}
      </div>

      <ul className="flex flex-wrap justify-center gap-x-4 gap-y-1 px-3 py-3 font-body text-tiny text-ink-muted">
        {LEGEND.map(({ label, swatch }) => (
          <li key={label} className="inline-flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-sm ${swatch}`} aria-hidden="true" />
            {label}
          </li>
        ))}
      </ul>

      <Tally {...tally} />
    </section>
  );
}
